import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { IPlacementRequirementKey } from 'app/models/Placement/PlacementDetails';
import { httpSuccess } from 'app/services/serviceHelpers';
import { fork, select } from 'redux-saga/effects';
import { SetPlacementFailRiskRequirementMessage } from 'store/redux-store/new-placement/saga';
import { placementSummaryActions } from '../slice';
import { IConsolidatedRequirementResponse } from '../init.types';
import { selectPlacementConsolidatedDetails } from '../consolidatedStoreSelectors';

type IStatusRequirementShape = {
  type: number;
  messageId?: number;
  messageVersion: string;
  data?: unknown;
  isValidated?: boolean;
};

export type IPlacementStatusRequirementHelperConfig<TRequirement extends IStatusRequirementShape = IStatusRequirementShape> = {
  consolidatedField: string; // assignment.<consolidatedField> in consolidatedDetails
  requirementKey: string; // response.data.<requirementKey>
  messageTypeId: IPlacementRequirementKey;
  getMessageId: (req: TRequirement) => number;
  requestError: { functionName: string; area: string };
  processError: { functionName: string; area: string };
};

export function* requestPlacementStatusRequirements(
  config: IPlacementStatusRequirementHelperConfig,
) {
  try {
    const consolidatedDetails = yield select(selectPlacementConsolidatedDetails);
    const assignment = consolidatedDetails?.summary?.assignment as any;
    const consolidated = assignment?.[config.consolidatedField];
    if (!consolidated) {
      return;
    }

    const response = {
      status: consolidated.status,
      data: consolidated.data,
    };

    if (httpSuccess(response?.status) && response?.data) {
      yield fork(commonPlacementStatusRequirement, response.data, config);
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: config.requestError.functionName,
        area: config.requestError.area,
      },
    });
  }
}

export function* processPlacementStatusRequirements(
  response: IConsolidatedRequirementResponse<unknown>,
  config: IPlacementStatusRequirementHelperConfig,
) {
  try {
    if (!httpSuccess(response.status) || !response.data) {
      return;
    }
    yield fork(commonPlacementStatusRequirement, response.data, config);
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: config.processError.functionName,
        area: config.processError.area,
      },
    });
  }
}

export function* commonPlacementStatusRequirement(
  data: any,
  config: IPlacementStatusRequirementHelperConfig,
) {
  const requirement = data?.[config.requirementKey] as IStatusRequirementShape | undefined;
  if (!requirement || requirement.isValidated) {
    return;
  }

  yield fork(SetPlacementFailRiskRequirementMessage, {
    payload: {
      messageId: config.getMessageId(requirement as any),
      version: requirement.messageVersion,
      passFailType: requirement.type,
      data: requirement.data,
      messageTypeId: config.messageTypeId,
      tooltipTypeParam: undefined,
      nextStatusRequirementParam: data?.nextStatusRequirement ?? [],
    },
    type: placementSummaryActions.setFailedRequirement.type,
  });
}
